import {useAccount, useGoogleAuth, useGoogleInfo, useModeration} from "@mytour/platform";
import {GOOGLE_AUTH_INSUFFICIENT_SCOPE} from "@mytour/shared";
import {Loading} from "@mytour/ui/Loading";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Trans as Translate} from "react-i18next";
import {Navigate, useSearchParams} from "react-router";
import {Alert, Button} from "reactstrap";

export function GoogleAuthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {account} = useAccount();
  const {isModeration} = useModeration();
  const {googleInfo} = useGoogleInfo();
  const {authenticateWithGoogle} = useGoogleAuth();
  const [hasInsufficientScope, setHasInsufficientScope] = useState(false);
  const attemptedCodes = useRef(new Set<string>());
  const currentCode = useRef<string | null>(null);
  const isMounted = useRef(false);
  const isGoogleAuthenticated =
    googleInfo?.hasCredentials ||
    account?.login === "e2e" ||
    account?.login === "e2e2"; /* Ne pas bloquer les comptes de test avec Google. */

  const code = searchParams.get("code");
  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useLayoutEffect(() => {
    currentCode.current = code;
    if (code === null || attemptedCodes.current.has(code)) {
      return;
    }
    attemptedCodes.current.add(code);
    setHasInsufficientScope(false);
    authenticateWithGoogle(code)
      .then(() => {
        if (isMounted.current && currentCode.current === code) {
          redirectHome();
        }
      })
      .catch((error: unknown) => {
        if (!isMounted.current || currentCode.current !== code) {
          return;
        }
        if (isInsufficientScopeError(error)) {
          setHasInsufficientScope(true);
          // Le code consommé ne doit pas être rejoué après un rechargement.
          setSearchParams({error: GOOGLE_AUTH_INSUFFICIENT_SCOPE}, {replace: true});
        } else {
          redirectHome();
        }
      });
  }, [authenticateWithGoogle, code, setSearchParams]);

  useEffect(() => {
    if (isModeration) {
      redirectHome();
    }
  }, [isModeration]);

  const hasAccessDenied =
    code === null &&
    searchParams.get("error") === "access_denied" &&
    !isGoogleAuthenticated &&
    !isModeration;
  const messageKey = hasAccessDenied ? "google.accessDenied" : "google.insufficientScope";

  if (
    hasAccessDenied ||
    hasInsufficientScope ||
    (code === null && searchParams.get("error") === GOOGLE_AUTH_INSUFFICIENT_SCOPE)
  ) {
    return (
      <div className="d-flex justify-content-center p-4">
        <Alert color="warning" className="text-center" style={{maxWidth: "480px"}}>
          <h5>
            <Translate i18nKey={`${messageKey}.title`}>
              {hasAccessDenied ? "Connexion Google interrompue" : "Agenda authorization missing"}
            </Translate>
          </h5>
          <p>
            <Translate i18nKey={`${messageKey}.message`}>
              {hasAccessDenied
                ? "La connexion à votre Agenda Google n’a pas abouti. Vous pouvez reprendre votre configuration."
                : "The connection failed because Agenda access was not granted."}
            </Translate>
          </p>
          <div
            className={
              hasAccessDenied
                ? "d-flex flex-wrap justify-content-center align-items-center gap-2"
                : undefined
            }
          >
            {googleInfo?.url && (
              <Button color="primary" tag="a" href={googleInfo.url}>
                <Translate i18nKey={`${messageKey}.retry`}>
                  {hasAccessDenied ? "Réessayer la connexion" : "Retry"}
                </Translate>
              </Button>
            )}
            {hasAccessDenied && (
              <Button color="link" tag="a" href="/">
                <Translate i18nKey="google.accessDenied.returnHome">Revenir à MyTour</Translate>
              </Button>
            )}
          </div>
        </Alert>
      </div>
    );
  }

  if (isGoogleAuthenticated) {
    return <Navigate to={"/home"} replace />;
  }

  return <Loading />;

  function redirectHome() {
    window.location.href = `${window.location.origin}`;
  }
}

function isInsufficientScopeError(error: unknown): boolean {
  const reason = (error as {response?: {data?: {reason?: string}}})?.response?.data?.reason;
  return reason === GOOGLE_AUTH_INSUFFICIENT_SCOPE;
}
